import {
  AppSessionId,
  HexVersionId,
  SavedViewParamType,
  StaticCellId,
  notEmpty,
  stableEmptyArray,
  stableEmptyObject,
  stableStringify,
  typedObjectEntries,
  typedObjectValues,
} from "@hex/common";
import { memoize } from "lodash";
import microMemoize from "micro-memoize";
import { shallowEqual } from "react-redux";
import * as R from "runtypes";

import {
  SharedFilterSessionStateMP,
  appSessionMPSelectors,
} from "../../../redux/slices/appSessionMPSlice.js";
import { hexVersionMPSelectors } from "../../../redux/slices/hexVersionMPSlice.js";
import { savedViewDefaultAppSessionSelectors } from "../../../redux/slices/savedViewDefaultAppSessionSlice.js";
import { RootState } from "../../../redux/store.js";
import { createSelector } from "../../../redux/utils/index.js";
import {
  HumanReadableInputUpdate,
  getCacheKeyForSelector,
  selectSavedViewsEnabled,
} from "../useGetViewSelectors.js";

import { getSharedFilterPreviewText } from "./useGetPersistedSFSelectors.js";

const SharedFilterStateIdWithStaticCellIdUniqueKey = R.String.withBrand(
  "SharedFilterStateIdWithStaticCellIdUniqueKey",
);
export type SharedFilterStateIdWithStaticCellIdUniqueKey = R.Static<
  typeof SharedFilterStateIdWithStaticCellIdUniqueKey
>;

/**
 * Stable stringified runtype for the key of the manually linked maps, so that we can compare differnt shared filters.
 * This is a runtype to improve readability of the selectors.
 */
const StableStringifiedStaticCellsKey = R.String.withBrand(
  "StableStringifiedStaticCellsKey",
);
export type StableStringifiedStaticCellsKey = R.Static<
  typeof StableStringifiedStaticCellsKey
>;

/**
 * Helper util method to determine if a session state is for a manually linked filter session.
 * Manually linked session states do not have an associated persisted filter.
 */
const isManuallyLinkedSessionState = (
  sessionState: SharedFilterSessionStateMP | null,
): boolean =>
  sessionState != null &&
  sessionState.filterId == null &&
  sessionState.manualLinks != null &&
  sessionState.deletedDate == null;

type ManuallyLinkedChangedInputWithIds = {
  sessionState: SharedFilterSessionStateMP;
  createdByDefaultSVAppSession: boolean; // True for the default manually linked filters in the SV, false for those created in the current appSession
  isChangedInCurrentSession: boolean; // if the shared filter was changed in the current session - False for default filters that are unchanged
  label: string;
  staticCellIds: StaticCellId[]; // all static cell ids impacted by the shared filter
  tooltip: string; // the tooltip to display for each value when hovering. This should include all of the cell labels.
};

/**
 * Object for manually linked shared filter session states that include all of the static cells impacted as well as the shared filter session states.
 */
type ManuallyLinkedSharedFilterSessionState = {
  staticCellIds: StaticCellId[];
  sharedFilterSessionStates: SharedFilterSessionStateMP[];
};

/**
 * A manually linked shared filter session state is a shared filter session state that was created entirely from an app session, with no related reference to a hex version.
 * Manually linked shared filter session states will store the related references in the manuallyLinked field with the staticCellIds.
 */
export const getManuallyLinkedSharedFilterSelectors = memoize(
  (hexVersionId: HexVersionId, appSessionId: AppSessionId) => {
    const selectSavedViewsEnabledInternal = (state: RootState): boolean =>
      selectSavedViewsEnabled(state, hexVersionId, appSessionId);

    /**
     * Memoized helper method that will cache the mapping from each static cell id to the list of shared filter session states that are manually linked to it
     * @argument sortedSharedFilterSessionStates - All of the current shared filter session states that should be added to the mapping from staticCellId to shared filter session states.
     * Note that we ask for a sorted mapping of shared filter session states to improve the cache hit rate.
     */
    const makeManuallyLinkedMapForSharedFilterSessionState = microMemoize(
      (sortedSharedFilterSessionStates: SharedFilterSessionStateMP[]) => {
        if (sortedSharedFilterSessionStates.length === 0) {
          return stableEmptyObject() as Record<
            StableStringifiedStaticCellsKey,
            ManuallyLinkedSharedFilterSessionState
          >;
        }

        const sharedFilterIdToMap: Record<
          StableStringifiedStaticCellsKey,
          ManuallyLinkedSharedFilterSessionState
        > = {};

        for (const sharedFilterSessionState of sortedSharedFilterSessionStates) {
          if (isManuallyLinkedSessionState(sharedFilterSessionState)) {
            // the static cell ids do not need to be sorted because they are added to a map.
            const staticCellIds = (
              sharedFilterSessionState.manualLinks?.map(
                (link) => link.staticCellId,
              ) ?? []
            ).sort();

            const hashedKey = StableStringifiedStaticCellsKey.check(
              stableStringify(staticCellIds),
            );
            const sharedFilterValue = sharedFilterIdToMap[hashedKey] ?? {
              staticCellIds,
              sharedFilterSessionStates: [],
            };
            sharedFilterValue.sharedFilterSessionStates.push(
              sharedFilterSessionState,
            );
            sharedFilterIdToMap[hashedKey] = sharedFilterValue;
          }
        }
        return sharedFilterIdToMap;
      },
      {
        maxSize: 2,
        // isEqual is used as a memoize option to determine if the arguments are the same or not. Since
        // we copy over the arguments into a sorted array, we use shallowEqual to determine if the arguments are the same.
        isEqual: shallowEqual,
      },
    );

    /**
     * Selects the saved view's default app session shared filter session states, taking into account
     * ONLY the manually linked shared filter session states (ignore any persisted shared filter states.)
     */
    const selectSVDefaultManuallyLinkedStaticCellIdToSharedFilterSessionState =
      createSelector(
        [
          selectSavedViewsEnabledInternal,
          savedViewDefaultAppSessionSelectors.selectSharedFilterSessionStates,
        ],
        (savedViewsEnabled, sharedFilterSessionStates) => {
          if (!savedViewsEnabled || sharedFilterSessionStates.length === 0) {
            return stableEmptyObject() as Record<
              StableStringifiedStaticCellsKey,
              ManuallyLinkedSharedFilterSessionState
            >;
          }
          return makeManuallyLinkedMapForSharedFilterSessionState(
            [...sharedFilterSessionStates].sort((a, b) =>
              a.id.localeCompare(b.id),
            ),
          );
        },
      );

    /**
     * The same process as @selectSVDefaultManuallyLinkedStaticCellIdToSharedFilterSessionState, but for the current app session.
     */
    const selectCurrentManuallyLinkedStaticCellIdToSharedFilterSessionState =
      createSelector(
        [
          selectSavedViewsEnabledInternal,
          appSessionMPSelectors.getSharedFilterSessionStateSelectors(
            appSessionId,
          ).selectEntities,
        ],
        (savedViewsEnabled, sharedFilterSessionStates) => {
          if (!savedViewsEnabled || sharedFilterSessionStates == null) {
            return stableEmptyObject() as Record<
              StableStringifiedStaticCellsKey,
              ManuallyLinkedSharedFilterSessionState
            >;
          }

          const sortedFilteredSessionStates = typedObjectValues(
            sharedFilterSessionStates,
          )
            .filter(notEmpty)
            .sort((a, b) => a.id.localeCompare(b.id));

          return makeManuallyLinkedMapForSharedFilterSessionState(
            sortedFilteredSessionStates,
          );
        },
      );

    /**
     * Memoized helper method that will cache the mapping from each static cell id to the list of shared filter session states that are manually linked to it
     */
    const getHumanReadableManuallyLinkedState = microMemoize(
      ({
        cellLabel,
        createdByDefaultSVAppSession,
        isChangedInCurrentSession,
        sessionState: { id: sessionStateId, manualLinks, operator, value },
        staticCellIds,
        tooltip,
      }: {
        sessionState: SharedFilterSessionStateMP;
        createdByDefaultSVAppSession: boolean;
        isChangedInCurrentSession: boolean;
        cellLabel: string;
        staticCellIds: StaticCellId[];
        tooltip: string;
      }): HumanReadableInputUpdate => {
        const humanReadableFormat = getSharedFilterPreviewText({
          name: null,
          operator,
          value,
          autoLinks: null,
          manualLinks,
        });
        const staticCellIdsString = staticCellIds.join(", ");
        // Use a unique identifier for the human readable mapping.
        const id = SharedFilterStateIdWithStaticCellIdUniqueKey.check(
          `${staticCellIdsString}-${sessionStateId}`,
        );
        return {
          id,
          label: cellLabel ?? "",
          humanReadableCurrentValue: createdByDefaultSVAppSession
            ? null
            : humanReadableFormat,
          humanReadableDefaultValue: createdByDefaultSVAppSession
            ? humanReadableFormat
            : null,
          isChangedInCurrentSession,
          paramType: SavedViewParamType.SHARED_FILTER,
          tooltip,
        };
      },
      {
        maxSize: 50,
      },
    );

    const selectChangedManuallyLinkedStaticCellIdToSessionStates =
      createSelector(
        [
          selectSavedViewsEnabledInternal,
          hexVersionMPSelectors.getCellContentAwareSelectors(hexVersionId)
            .selectStaticCellIdToLabel,
          hexVersionMPSelectors.getCellContentSelectors(hexVersionId)
            .selectEntitiesByCellContentsId,
          selectCurrentManuallyLinkedStaticCellIdToSharedFilterSessionState,
          selectSVDefaultManuallyLinkedStaticCellIdToSharedFilterSessionState,
        ],
        (
          savedViewsEnabled,
          labelsByStaticCellId,
          cellContents,
          currentSharedFilterSessionStates,
          savedViewDefaultSharedFilterSessionStates,
        ): readonly ManuallyLinkedChangedInputWithIds[] => {
          if (!cellContents || !savedViewsEnabled) {
            return stableEmptyArray<ManuallyLinkedChangedInputWithIds>();
          }
          const changedSharedFilters: ManuallyLinkedChangedInputWithIds[] = [];
          const defaultManualLinkHashesInCurrentSession: string[] = [];
          // Iterate through the mapping of {staticCellId: sessionStates} for the current / saved default app sessions to determine if any of the
          // filter states have changed.
          // Because we have no persisted key for manually linked filters, we create our own "key" by hashing the hash(operator-options-value) for the manual links.
          // Once we have the set of hashed "keys", we check that both app sessions have the keys and if not, added the static id to the changed list.
          for (const [staticCellIdHashedKey, currentData] of typedObjectEntries(
            currentSharedFilterSessionStates,
          )) {
            if (currentData == null) {
              continue;
            }
            const { sharedFilterSessionStates, staticCellIds } = currentData;
            const defaultSharedFilterSessionState =
              savedViewDefaultSharedFilterSessionStates[staticCellIdHashedKey];

            const hashedManualLinksForCurrentMap: Map<
              StableStringifiedStaticCellsKey,
              SharedFilterSessionStateMP
            > = new Map(
              sharedFilterSessionStates?.map((state) => {
                // We must use stableStringify if we want to use the hashed manual links as a stable key.
                const hashedManualLink = stableStringify({
                  operator: state.operator,
                  options: state.options,
                  value: state.value,
                });
                return [
                  hashedManualLink as StableStringifiedStaticCellsKey, // stableStringify returns a string, so we need to cast the runtype
                  state,
                ];
              }),
            );

            const hashedManualLinksForDefaultView =
              defaultSharedFilterSessionState?.sharedFilterSessionStates?.map(
                (state) => {
                  return stableStringify({
                    operator: state.operator,
                    options: state.options,
                    value: state.value,
                  });
                },
              ) ?? [];

            for (const [
              hashedManualLink,
              result,
            ] of hashedManualLinksForCurrentMap) {
              const impactedCells = staticCellIds.map(
                (id) => labelsByStaticCellId[id],
              );
              // If the manualLink is not in the default, it's new and has been added
              if (!hashedManualLinksForDefaultView.includes(hashedManualLink)) {
                changedSharedFilters.push({
                  staticCellIds,
                  sessionState: result,
                  createdByDefaultSVAppSession: false,
                  isChangedInCurrentSession: true,
                  // The label for a manually linked shared filter is generic, and on hover you can see all of the changes cells.
                  label: `Project filter for ${impactedCells.length} cell${impactedCells.length > 1 ? "s" : ""}`,
                  tooltip: impactedCells.join(", "),
                });
                // If the manualLink is in the default, it's one of the default filters on the saved view and unchanged in the current session
              } else {
                defaultManualLinkHashesInCurrentSession.push(hashedManualLink);
                changedSharedFilters.push({
                  staticCellIds,
                  sessionState: result,
                  createdByDefaultSVAppSession: true,
                  isChangedInCurrentSession: false,
                  label: `Project filter for ${impactedCells.length} cell${impactedCells.length > 1 ? "s" : ""}`,
                  tooltip: impactedCells.join(", "),
                });
              }
            }
          }

          // Iterate through the default shared filter session states to show any filters that have been removed.
          for (const defaultData of typedObjectValues(
            savedViewDefaultSharedFilterSessionStates,
          )) {
            if (defaultData == null) {
              continue;
            }
            const { sharedFilterSessionStates, staticCellIds } = defaultData;
            const hashedManualLinksForDefaultMap: Map<
              StableStringifiedStaticCellsKey,
              SharedFilterSessionStateMP
            > = new Map(
              sharedFilterSessionStates?.map((state) => {
                // We must use stableStringify if we want to use the hashed manual links as a stable key.
                const hashedManualLink = stableStringify({
                  operator: state.operator,
                  options: state.options,
                  value: state.value,
                });
                return [
                  hashedManualLink as StableStringifiedStaticCellsKey, // stableStringify returns a string, so we need to cast the runtype
                  state,
                ];
              }),
            );

            for (const [
              hashedManualLink,
              sessionState,
            ] of hashedManualLinksForDefaultMap) {
              if (
                !defaultManualLinkHashesInCurrentSession.includes(
                  hashedManualLink,
                )
              ) {
                const impactedCells = staticCellIds.map(
                  (id) => labelsByStaticCellId[id],
                );
                changedSharedFilters.push({
                  staticCellIds,
                  sessionState,
                  createdByDefaultSVAppSession: true,
                  isChangedInCurrentSession: true,
                  label: `Project filter for ${impactedCells.length} cell${impactedCells.length > 1 ? "s" : ""}`,
                  tooltip: impactedCells.join(", "),
                });
              }
            }
          }

          return changedSharedFilters;
        },
      );

    const selectChangedCellIds = createSelector(
      [
        selectChangedManuallyLinkedStaticCellIdToSessionStates,
        hexVersionMPSelectors.getCellSelectors(hexVersionId)
          .selectAllStaticCellIdToCellIds,
      ],
      (changedManuallyLinkedSharedFilterStates, staticCellIdToCellIdMap) => {
        const flattenedStaticCellIds = [
          ...new Set(
            changedManuallyLinkedSharedFilterStates
              .filter(
                ({ isChangedInCurrentSession }) => isChangedInCurrentSession,
              )
              .map(({ staticCellIds }) => staticCellIds)
              .flat(),
          ),
        ];

        return flattenedStaticCellIds
          .map((id) => staticCellIdToCellIdMap?.[id])
          .filter(notEmpty);
      },
    );
    /**
     * Keep the human readable diff outputs separately, so that we can display them in the UI *only* when the popover is needed.
     * The above selectChangedCellIds method can use the selector prior to computing the human readable diff values.
     */
    const selectChangedManuallyLinkedHumanReadableDiffValues = createSelector(
      [selectChangedManuallyLinkedStaticCellIdToSessionStates],
      (changedManuallyLinkedSharedFilterStates) => {
        return changedManuallyLinkedSharedFilterStates.map(
          ({
            createdByDefaultSVAppSession,
            isChangedInCurrentSession,
            label,
            sessionState,
            staticCellIds,
            tooltip,
          }) =>
            getHumanReadableManuallyLinkedState({
              cellLabel: label,
              createdByDefaultSVAppSession,
              isChangedInCurrentSession,
              sessionState,
              staticCellIds,
              tooltip,
            }),
        );
      },
    );

    return {
      selectChangedCellIdsForManuallyLinkedFilters: selectChangedCellIds,
      selectChangedManuallyLinkedHumanReadableDiffValues,
    };
  },

  (hexVersionId, appSessionId) =>
    getCacheKeyForSelector(hexVersionId, appSessionId),
);
